"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.createSkillsEditDialog = createSkillsEditDialog;
var _dialog = require("../../ui/dialog");
var _litHtml = require("lit-html");
require("solid-ui/components/actions/button");
require("solid-ui/components/forms/combobox");
require("../../styles/EditDialogs.css");
require("../contactInfo/ContactInfoEditDialog.css");
var _rowState = require("../shared/rowState");
var _textUtils = require("../../textUtils");
var _mutations = require("./mutations");
var _profileIcons = require("../../icons-svg/profileIcons");
var _texts = require("../../texts");
const ESCO_SKILL_SEARCH_URI = 'https://ec.europa.eu/esco/api/search?language=$(language)&limit=$(limit)&type=skill&text=$(name)&selectedVersion=v1.2.0';
const ESCO_SEARCH_LANGUAGE = 'en';
const ESCO_SEARCH_LIMIT = 8;
const ESCO_SKILL_BASE_URI = 'http://data.europa.eu/esco/skill/';
function normalizeSkillPublicId(value) {
  const normalized = sanitizeSkillFieldValue(value);
  if (!normalized) return '';
  if (normalized.startsWith('_:')) return normalized;
  if (normalized.startsWith('skill:')) return normalized;
  if (normalized.startsWith(ESCO_SKILL_BASE_URI)) {
    const suffix = normalized.slice(ESCO_SKILL_BASE_URI.length);
    return suffix ? `skill:${suffix}` : normalized;
  }
  return normalized;
}
function buildEscoSkillSearchUrl(name) {
  return ESCO_SKILL_SEARCH_URI.replace('$(language)', encodeURIComponent(ESCO_SEARCH_LANGUAGE)).replace('$(limit)', encodeURIComponent(String(ESCO_SEARCH_LIMIT))).replace('$(name)', encodeURIComponent(name));
}
function toSkillLabel(result) {
  return result?.title || result?.searchHit || result?.preferredLabel?.en || result?.uri || '';
}
async function fetchEscoSkillSuggestions(name) {
  const query = sanitizeSkillFieldValue(name);
  if (query.length < 2 || typeof fetch !== 'function') return [];
  try {
    const response = await fetch(buildEscoSkillSearchUrl(query), {
      headers: {
        Accept: 'application/json'
      }
    });
    if (!response.ok) return [];
    const payload = await response.json();
    const results = Array.isArray(payload?._embedded?.results) ? payload._embedded.results : [];
    const seen = new Set();
    return results.map(result => {
      const label = sanitizeSkillFieldValue(toSkillLabel(result));
      const publicId = normalizeSkillPublicId(typeof result?.uri === 'string' ? result.uri : '');
      return {
        label,
        publicId
      };
    }).filter(suggestion => {
      if (!suggestion.label) return false;
      const key = suggestion.label.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  } catch {
    return [];
  }
}
function sanitizeSkillFieldValue(value) {
  return (0, _textUtils.sanitizeTextValue)(value);
}
function rowHasContent(row) {
  return [row.name].some(_textUtils.hasNonEmptyText);
}
function toFormState(details) {
  const rows = (details || []).map(detail => ({
    name: sanitizeSkillFieldValue((0, _textUtils.toText)(detail.name)),
    publicId: normalizeSkillPublicId((0, _textUtils.toText)(detail.publicId)),
    entryNode: (0, _textUtils.toText)(detail.entryNode),
    status: (0, _textUtils.toText)(detail.entryNode) ? 'existing' : 'new'
  })).filter(row => Boolean(row.name || row.publicId || row.entryNode));
  return {
    skills: rows.length ? rows : [{
      name: '',
      publicId: '',
      entryNode: '',
      status: 'new'
    }]
  };
}
function toSkillComboboxOption(suggestion) {
  return {
    label: suggestion.label,
    value: suggestion.publicId,
    publicId: suggestion.publicId
  };
}
function readSkillComboboxInputValue(event) {
  const customEvent = event;
  if (typeof customEvent.detail?.value === 'string') {
    return customEvent.detail.value;
  }
  const target = event.target;
  return typeof target?.value === 'string' ? target.value : '';
}
function readSkillComboboxChange(event) {
  const customEvent = event;
  if (customEvent.detail?.option) {
    return customEvent.detail.option;
  }
  return null;
}
function createSkillSuggestionProvider() {
  return async query => {
    const suggestions = await fetchEscoSkillSuggestions(query);
    return suggestions.map(toSkillComboboxOption);
  };
}
function initializeSkillComboboxes(form, rows) {
  const comboboxElements = form.querySelectorAll('solid-ui-combobox[data-skill-row-index]');
  comboboxElements.forEach(comboboxElement => {
    const rowIndex = Number(comboboxElement.dataset.skillRowIndex);
    if (Number.isNaN(rowIndex)) return;
    const row = rows[rowIndex];
    const nextInputValue = row?.name || '';
    const options = row?.publicId && row?.name ? [{
      label: row.name,
      value: row.publicId,
      publicId: row.publicId
    }] : [];
    comboboxElement.suggestionProvider = createSkillSuggestionProvider();
    comboboxElement.options = options;
    comboboxElement.value = row?.publicId || '';
    comboboxElement.inputValue = nextInputValue;
    comboboxElement.label = '';
    comboboxElement.placeholder = 'Skill';
    const comboboxInput = comboboxElement.shadowRoot?.querySelector('input');
    if (comboboxInput) {
      comboboxInput.value = nextInputValue;
    }
  });
}
function syncSkillRowsFromComboboxes(form, rows) {
  const comboboxElements = form.querySelectorAll('solid-ui-combobox[data-skill-row-index]');
  comboboxElements.forEach(comboboxElement => {
    const rowIndex = Number(comboboxElement.dataset.skillRowIndex);
    if (Number.isNaN(rowIndex) || !rows[rowIndex]) return;
    const comboboxInput = comboboxElement.shadowRoot?.querySelector('input');
    const nextName = sanitizeSkillFieldValue(comboboxInput?.value || comboboxElement.inputValue || '');
    const nextPublicId = normalizeSkillPublicId(comboboxElement.value || '');
    (0, _rowState.applyRowFieldChange)(rows[rowIndex], 'name', nextName, rowHasContent);
    rows[rowIndex].publicId = nextPublicId;
  });
}
function validateSkillsBeforeSave(_rows) {
  return null;
}
function hasInvalidSkillSelection(_rows) {
  return false;
}
function updateSkillsSubmitEnabled(rows) {
  const submitButton = (0, _dialog.getSharedDialogSaveButton)(document);
  if (!submitButton) return;
  submitButton.disabled = hasInvalidSkillSelection(rows);
}
function focusSkillField(form, selector) {
  const nextField = form.querySelector(selector);
  if (!nextField || typeof nextField.focus !== 'function') return;
  if (typeof nextField.scrollIntoView === 'function') {
    nextField.scrollIntoView({
      block: 'start',
      behavior: 'auto'
    });
  }
  const comboboxHost = nextField.tagName === 'SOLID-UI-COMBOBOX' ? nextField : null;
  const comboboxInput = comboboxHost ? comboboxHost.shadowRoot?.querySelector('input') : null;
  const focusTarget = comboboxInput || nextField;
  focusTarget.focus();
  if (focusTarget instanceof HTMLInputElement) {
    const caretPosition = 0;
    focusTarget.setSelectionRange(caretPosition, caretPosition);
    focusTarget.scrollLeft = 0;
    requestAnimationFrame(() => {
      focusTarget.scrollLeft = 0;
    });
  }
  if (comboboxHost?._closePopup) {
    requestAnimationFrame(() => {
      comboboxHost._closePopup?.();
    });
  }
}
function renderSkillInputRow({
  rows,
  index,
  displayIndex,
  onDelete
}) {
  const label = `Skill ${displayIndex + 1}`;
  const handleSkillInput = _field => e => {
    const nextValue = sanitizeSkillFieldValue(readSkillComboboxInputValue(e));
    if (rows[index]) {
      (0, _rowState.applyRowFieldChange)(rows[index], 'name', nextValue, rowHasContent);
      rows[index].publicId = '';
      updateSkillsSubmitEnabled(rows);
    }
  };
  const handleSkillChange = e => {
    const selectedOption = readSkillComboboxChange(e);
    if (!rows[index] || !selectedOption?.publicId) return;
    (0, _rowState.applyRowFieldChange)(rows[index], 'name', sanitizeSkillFieldValue(selectedOption.label), rowHasContent);
    rows[index].publicId = selectedOption.publicId;
    updateSkillsSubmitEnabled(rows);
  };
  const handleDelete = event => {
    event.preventDefault();
    onDelete();
  };
  return (0, _litHtml.html)`
    <div class="profile-edit-dialog__row profile-edit-dialog__row--full profile-edit-dialog__row--skill">
      <label aria-label=${`${label} Name`} class="label profile-edit-dialog__field profile-edit-dialog__field--full">
        <solid-ui-combobox
          aria-label=${`${label} Name`}
          data-skill-row-index=${String(index)}
          @input=${handleSkillInput('name')}
          @change=${handleSkillChange}
        ></solid-ui-combobox>
        <small class="profile-edit-dialog__input-help-text">Type to search ESCO, or enter your own skill.</small>
      </label>
      <div class="profile-edit-dialog__actions profile-edit-dialog__actions--edge">
        <solid-ui-button
          type="button"
          variant="icon"
          size="md"
          class="profile-edit-dialog__delete-button"
          aria-label=${`Delete skill ${displayIndex + 1}`}
          title=${_texts.deleteEntryButtonTitleText}
          @click=${handleDelete}
        >
          <span slot="icon" class="profile-edit-dialog__delete-icon" aria-hidden="true">${_profileIcons.trashIcon}</span>
        </solid-ui-button>
      </div>
    </div>
  `;
}
function renderSkillsSection(rows, onAddRow) {
  const visibleRows = rows.map((row, index) => ({
    row,
    index
  })).filter(({
    row
  }) => row.status !== 'deleted');
  return (0, _litHtml.html)`
    <section class="profile-edit-dialog__section" aria-label="Skills">
      <fieldset>
        <legend class="sr-only">Skill entries</legend>
        ${visibleRows.map(({
    index
  }, displayIndex) => renderSkillInputRow({
    rows,
    index,
    displayIndex,
    onDelete: () => {
      (0, _rowState.deleteRow)(rows, index);
      onAddRow();
    }
  }))}
      </fieldset>
    </section>
  `;
}
function renderSkillsEditTemplate(form, formState, viewerMode, options = {}) {
  const rerender = (nextOptions = {}) => {
    syncSkillRowsFromComboboxes(form, formState.skills);
    renderSkillsEditTemplate(form, formState, viewerMode, nextOptions);
  };
  (0, _litHtml.render)((0, _litHtml.html)`
    ${renderSkillsSection(formState.skills, rerender)}
    ${viewerMode !== 'owner' ? (0, _litHtml.html)`<p class="profile-edit-dialog__login-message">${_texts.ownerLoginRequiredDialogMessageText}</p>` : null}
  `, form);
  initializeSkillComboboxes(form, formState.skills);
  updateSkillsSubmitEnabled(formState.skills);
  if (options.focusSelector) {
    focusSkillField(form, options.focusSelector);
  }
}
function createSkillsEditForm(details, viewerMode) {
  const form = document.createElement('form');
  form.classList.add('profile__edit-form');
  const formState = toFormState(details);
  renderSkillsEditTemplate(form, formState, viewerMode);
  const addRow = () => {
    syncSkillRowsFromComboboxes(form, formState.skills);
    formState.skills.unshift({
      name: '',
      publicId: '',
      entryNode: '',
      status: 'new'
    });
    renderSkillsEditTemplate(form, formState, viewerMode, {
      focusSelector: '[data-skill-row-index="0"]'
    });
  };
  return {
    form,
    formState,
    addRow
  };
}
async function createSkillsEditDialog(event, store, subject, skills, viewerMode, onSaved) {
  const dom = event.currentTarget?.ownerDocument || document;
  const {
    form,
    formState,
    addRow
  } = createSkillsEditForm(skills, viewerMode);
  const result = await (0, _dialog.openInputDialog)({
    title: _texts.editSkillsDialogTitleText,
    dom,
    form,
    onOpen: () => focusSkillField(form, '[data-skill-row-index="0"]'),
    shouldCloseWithoutSave: () => {
      syncSkillRowsFromComboboxes(form, formState.skills);
      const ops = (0, _rowState.summarizeRowOps)(formState.skills, rowHasContent);
      return ops.create.length === 0 && ops.update.length === 0 && ops.remove.length === 0;
    },
    headerAction: {
      type: 'button',
      label: '+ Add More',
      ariaLabel: 'Add another skill',
      onClick: addRow
    },
    submitLabel: _texts.dialogSubmitLabelText,
    cancelLabel: _texts.dialogCancelLabelText,
    validate: () => {
      syncSkillRowsFromComboboxes(form, formState.skills);
      if (viewerMode !== 'owner') {
        return _texts.ownerLoginRequiredDialogMessageText;
      }
      return validateSkillsBeforeSave(formState.skills);
    },
    onSave: async () => {
      syncSkillRowsFromComboboxes(form, formState.skills);
      const skillOps = (0, _rowState.summarizeRowOps)(formState.skills, rowHasContent);
      const plan = {
        create: skillOps.create,
        update: skillOps.update,
        remove: skillOps.remove
      };
      await (0, _mutations.processSkillsMutations)(store, subject, plan);
    },
    formatSaveError: error => {
      return error instanceof Error ? error.message : _texts.saveSkillsUpdatesFailedMessageText;
    }
  });
  if (!result) return;
  if (onSaved) {
    await onSaved();
  }
}